import { memo, useEffect, useRef, useState, type MouseEvent } from 'react';
import { Handle, Position, type Node, type NodeProps } from '@xyflow/react';
import Badge from 'react-bootstrap/Badge';
import SubtleBadge from '@/components/common/SubtleBadge';
import Icon from './Icon';
import {
  difficultyBadge,
  formatVolume,
  mapIntent,
} from '../utils/seoMetrics';
import type { ClusterNodeData } from '../types';

type ClusterFlowNode = Node<ClusterNodeData, 'cluster'>;

function stopNodePointer(event: MouseEvent) {
  event.stopPropagation();
}

function AddNodeStub({
  side,
  compact,
  onAdd
}: {
  side: 'left' | 'right';
  compact: boolean;
  onAdd: () => void;
}) {
  return (
    <div
      className={`cluster-node__add-stub cluster-node__add-stub--${side}${
        compact ? ' cluster-node__add-stub--compact' : ''
      } nodrag nopan nowheel`}
      dir="ltr"
    >
      <div className="cluster-node__add-line" />
      <button
        type="button"
        className="cluster-node__add-btn"
        aria-label="افزودن نود"
        title="افزودن نود"
        onClick={event => {
          stopNodePointer(event);
          onAdd();
        }}
        onMouseDown={stopNodePointer}
      >
        <Icon name="plus-lg" size="0.62rem" />
      </button>
    </div>
  );
}

function ClusterNode({ id, data, selected }: NodeProps<ClusterFlowNode>) {
  const seo = data.seo;
  const depth = Math.min(data.depth, 4);
  const isClusterDepth = !data.isLeaf && depth <= 2;
  const branchSide = data.branchSide ?? 'left';
  const opensRight = branchSide === 'right';
  const isCenter = branchSide === 'center';

  const isRoot = isCenter || data.depth === 0;
  const canEdit = Boolean(data.canEdit && !data.locked);
  const showDelete = canEdit && !isRoot && Boolean(data.onDelete);
  const showAdd = canEdit && Boolean(data.onAddChild);
  const [hovered, setHovered] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimer = () => {
    if (!hideTimerRef.current) return;
    window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  };

  const showControls = () => {
    clearHideTimer();
    setHovered(true);
  };

  const hideControls = () => {
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      setHovered(false);
      hideTimerRef.current = null;
    }, 180);
  };

  useEffect(() => () => clearHideTimer(), []);

  const classes = [
    'cluster-node position-relative w-100 d-flex align-items-center rounded-2 cursor-pointer text-body',
    hovered ? 'cluster-node--show-controls' : '',
    isRoot ? 'cluster-node--root' : 'border shadow-sm bg-body',
    data.compact ? 'cluster-node--compact py-1 pe-2 ps-4' : 'py-2 pe-2 ps-4',
    data.isLeaf
      ? 'cluster-node--leaf border-success'
      : !isRoot && isClusterDepth
        ? 'cluster-node--cluster border-primary'
        : '',
    data.isMatch ? 'cluster-node--match border-warning' : '',
    data.isDimmed ? 'opacity-25' : '',
    data.isOnPath || selected
      ? isRoot
        ? 'cluster-node--selected cluster-node--selected-root'
        : 'cluster-node--selected border-primary'
      : '',
    data.isPinned ? 'cluster-node--pinned' : '',
    selected ? 'cluster-node--lift' : '',
    opensRight ? 'cluster-node--opens-right' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      dir="rtl"
      onMouseEnter={showControls}
      onMouseLeave={hideControls}
    >
      {showDelete ? (
        <div
          className="cluster-node__toolbar nodrag nopan nowheel"
          onMouseEnter={showControls}
        >
          <button
            type="button"
            className="cluster-node__delete"
            aria-label="حذف نود"
            title="حذف نود"
            onClick={event => {
              stopNodePointer(event);
              data.onDelete?.(id);
            }}
            onMouseDown={stopNodePointer}
          >
            <Icon name="trash" size="0.7rem" />
          </button>
        </div>
      ) : null}

      {showAdd && (isCenter || !opensRight) ? (
        <AddNodeStub
          side="left"
          compact={Boolean(data.compact)}
          onAdd={() => data.onAddChild?.(id)}
        />
      ) : null}

      {/* Left-opening branches / root left side */}
      {(isCenter || !opensRight) && (
        <Handle
          type="source"
          id="out-left"
          position={Position.Left}
          className="cluster-handle"
        />
      )}
      {opensRight && (
        <Handle
          type="target"
          id="in-left"
          position={Position.Left}
          className="cluster-handle"
        />
      )}

      {data.canExpand && !data.locked ? (
        <button
          type="button"
          className={`cluster-node__toggle${opensRight ? ' cluster-node__toggle--right' : ''}`}
          aria-label={data.isExpanded ? 'بستن زیرشاخه' : 'باز کردن زیرشاخه'}
          onClick={(event) => {
            event.stopPropagation();
            data.onToggle?.(id);
          }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <Icon name={data.isExpanded ? 'dash-lg' : 'plus-lg'} />
        </button>
      ) : null}

      <div className="w-100 min-w-0">
        <div className="d-flex align-items-center gap-1 mb-1">
          {data.isLeaf ? (
            <span className="fs--2 text-600 fw-medium">کلمه</span>
          ) : (
            <span className="d-inline-flex align-items-center gap-1 fs--2 text-600 fw-medium">
              خوشه
              <Badge
                bg="secondary"
                pill
                className="fw-medium"
                style={{ minWidth: '0.95rem', fontSize: '0.625rem' }}
              >
                {data.childCount}
              </Badge>
            </span>
          )}
          {data.isPinned ? (
            <span className="ms-auto text-warning d-inline-flex" title="پین‌شده">
              <Icon name="pin-angle-fill" size="0.7rem" />
            </span>
          ) : null}
        </div>

        <div
          className="fw-semi-bold text-body fs--1"
          title={data.label}
          style={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: data.compact ? 1 : 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.35,
          }}
        >
          {data.label}
        </div>

        {seo && data.showBadges !== false ? (
          <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
            <Badge
              bg="primary"
              pill
              className="text-white d-inline-flex align-items-center gap-1 px-2 py-1"
              style={{ fontSize: '0.7rem', lineHeight: 1.25 }}
              title={
                data.isLeaf
                  ? 'میزان جستجو'
                  : 'میزان جستجو (مجموع کلمات خوشه)'
              }
            >
              <Icon name="bar-chart" size="0.5rem" />
              {formatVolume(seo.search_volume_miztols)}
              {!data.isLeaf ? <span className="opacity-75">Σ</span> : null}
            </Badge>
            {seo.difficulty != null ? (
              <span title="سختی کلمه کلیدی (KD)">
                <SubtleBadge
                  bg={difficultyBadge(seo.difficulty)}
                  pill
                  className="d-inline-flex align-items-center px-2 py-1"
                  style={{ fontSize: '0.7rem', lineHeight: 1.25 }}
                >
                  KD {seo.difficulty}
                </SubtleBadge>
              </span>
            ) : null}
            {seo.len != null ? (
              <span
                title={
                  data.isLeaf ? 'طول کلمات' : 'میانگین طول کلمات'
                }
              >
                <SubtleBadge
                  bg="info"
                  pill
                  className="d-inline-flex align-items-center gap-1 px-2 py-1"
                  style={{ fontSize: '0.7rem', lineHeight: 1.25 }}
                >
                  L{seo.len}
                  {!data.isLeaf ? (
                    <span className="opacity-75" aria-hidden>
                      x̄
                    </span>
                  ) : null}
                </SubtleBadge>
              </span>
            ) : null}
            {(seo.intent || []).slice(0, 2).map((code) => {
              const item = mapIntent(code);
              return (
                <span key={code} title={`نیت: ${item.label}`}>
                  <SubtleBadge
                    bg={item.tone}
                    pill
                    className="d-inline-flex align-items-center px-2 py-1"
                    style={{ fontSize: '0.7rem', lineHeight: 1.25 }}
                  >
                    {item.short}
                  </SubtleBadge>
                </span>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Right-opening branches / root right side */}
      {(isCenter || opensRight) && (
        <Handle
          type="source"
          id="out-right"
          position={Position.Right}
          className="cluster-handle"
        />
      )}
      {!opensRight && !isCenter && (
        <Handle
          type="target"
          id="in-right"
          position={Position.Right}
          className="cluster-handle"
        />
      )}

      {showAdd && (isCenter || opensRight) ? (
        <AddNodeStub
          side="right"
          compact={Boolean(data.compact)}
          onAdd={() => data.onAddChild?.(id)}
        />
      ) : null}
    </div>
  );
}

export default memo(ClusterNode);
